export const PS_RATING_MIN = -3
export const PS_RATING_MAX = 3
export const PS_RATING_STEP = 0.001
/** MLB PS ratings are mean-zero; |sum| should stay within this after rounding. */
export const PS_LEAGUE_SUM_TOLERANCE = 0.01

export function roundRating(value: number): number {
  return Math.round(value * 1000) / 1000
}

export function applySingleTeamAdjustment(
  effective: Record<string, number>,
  team: string,
  newRating: number,
): Record<string, number> {
  if (!(team in effective)) {
    throw new Error(`unknown team: ${team}`)
  }
  const teams = Object.keys(effective)
  if (teams.length < 2) {
    throw new Error('need at least two teams')
  }
  const oldT = effective[team]
  const newT = roundRating(newRating)
  const delta = newT - oldT
  const share = -delta / (teams.length - 1)
  const out: Record<string, number> = { [team]: newT }
  for (const t of teams) {
    if (t === team) continue
    out[t] = roundRating(effective[t] + share)
  }
  return reconcileZeroSum(out)
}

export function effectiveMapFromRows(
  rows: ReadonlyArray<{ team: string; effective_rating: number }>,
): Record<string, number> {
  return Object.fromEntries(rows.map((r) => [r.team, r.effective_rating]))
}

export function leagueSum(ratings: Record<string, number>): number {
  return Object.values(ratings).reduce((acc, v) => acc + v, 0)
}

export function leagueMean(ratings: Record<string, number>): number {
  const vals = Object.values(ratings)
  if (!vals.length) return 0
  return leagueSum(ratings) / vals.length
}

export function isZeroSumBalanced(
  ratings: Record<string, number>,
  tolerance = PS_LEAGUE_SUM_TOLERANCE,
): boolean {
  return Math.abs(leagueSum(ratings)) <= tolerance
}

/** Mirror backend: nudge ratings so league sum is ~0 after decimal rounding. */
export function reconcileZeroSum(ratings: Record<string, number>): Record<string, number> {
  const teams = Object.keys(ratings)
  if (!teams.length) return {}
  const out: Record<string, number> = {}
  for (const t of teams) {
    out[t] = roundRating(ratings[t]!)
  }
  let residual = leagueSum(out)
  if (Math.abs(residual) < 1e-12) return out
  const share = roundRating(-residual / teams.length)
  for (const t of teams) {
    out[t] = roundRating(out[t]! + share)
  }
  residual = leagueSum(out)
  if (Math.abs(residual) > 1e-12) {
    const first = teams[0]!
    out[first] = roundRating(out[first]! - residual)
  }
  return out
}
